"use client";

import React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import mail from "../../public/mail-icon.png";
import phone from "../../public/Icon-call.png";
import user from "../../public/user-icon.svg";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { AppDispatch } from "@/lib/redux/store";
import { useDispatch } from "react-redux";
import { postEnquiry } from "@/lib/redux/slice/propertySlice";
import { EnquiryPayload, UnitInterest } from "@/interfaces/propertyinterface";
import { FlatType } from "@/interfaces/propertyMetadataInterface";
import { getFlatCategories } from "@/lib/redux/slice/flatSlice";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  enquirySchema,
  EnquiryValidationType,
} from "@/validations/enquiryFormSchema";
import { toast } from "react-toastify";
import axios from "axios";
import { reportLead } from "@/lib/metaPixel";

const EnquiryForm = ({
  onClose,
  propertyId,
  unitInterest,
}: {
  onClose: () => void;
  propertyId?: string;
  unitInterest?: UnitInterest;
}) => {
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<EnquiryValidationType>({
    resolver: yupResolver(enquirySchema),
    // onChange — not onBlur. iOS Safari frequently doesn't fire blur
    // events (Done key on keyboard, auto-fill, tap-outside dismiss).
    // With onBlur, fields would stay "untouched" from RHF's POV, isValid
    // would stay false, and the submit button would stay greyed out
    // forever. onChange validates on every keystroke so the button
    // lights up the moment criteria are met. Cheap for a 6-field form.
    mode: "onChange",
  });

  const dispatch = useDispatch<AppDispatch>();
  const [flatCategories, setFlatCategories] = useState<FlatType[]>([]);
  // PDPA: consent must be an affirmative act, so the box starts unticked.
  // Pre-ticked consent is weak under the Personal Data Protection Act and
  // reads as a dark pattern to exactly the cautious buyer this form wants.
  const [isPrivacyChecked, setIsPrivacyChecked] = useState<boolean>(false);

  const text =
    "By submitting this form, you are deemed to have agreed to our Privacy Policy and consent that we or our sales representative may contact you for the purpose of your enquiry via voice call, email, SMS, WhatsApp, regardless of your registration status with the DNS registry. (Personal Data Protection Act 2012)";

  const words = text.split(" ");
  const [isExpanded, setIsExpanded] = useState(false);

  const detailsRef = useRef<HTMLDetailsElement>(null);

  // Pull unit types intelligently:
  // - When the form is bound to a specific property (propertyId set), derive
  //   the type list from THAT property's actual units. This avoids the
  //   global flat-types endpoint, which returns one entry per (property,
  //   type) combination and shows hideous duplicates ("1 Bedroom" x10).
  // - When the form is generic (homepage auto-popup, no propertyId), fall
  //   back to the global list, deduplicated by normalised name.
  useEffect(() => {
    const normalise = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");
    const dedupeByName = (items: FlatType[]): FlatType[] => {
      const seen = new Map<string, FlatType>();
      for (const item of items) {
        const name = item?.name;
        if (!name) continue;
        const key = normalise(String(name));
        if (!seen.has(key)) seen.set(key, item);
      }
      return [...seen.values()];
    };

    const fetchFromProperty = async () => {
      try {
        const res = await axios.get(
          `${process.env.NEXT_PUBLIC_API_URL}/property-meta-data/meta-data-by-property-id/${propertyId}`
        );
        const units = Array.isArray(res.data) ? res.data : [];
        const types: FlatType[] = units
          .map((u: any) => u?.type)
          .filter((t: any): t is FlatType => Boolean(t?._id && t?.name));
        setFlatCategories(dedupeByName(types));
      } catch (error) {
        console.log("Error fetching property unit types, falling back to global", error);
        // Fall back to global on error
        const response = await dispatch(getFlatCategories());
        if (Array.isArray(response.payload)) {
          setFlatCategories(dedupeByName(response.payload));
        }
      }
    };

    const fetchGlobal = async () => {
      try {
        const response = await dispatch(getFlatCategories());
        if (Array.isArray(response.payload) && response.payload.length > 0) {
          setFlatCategories(dedupeByName(response.payload));
        }
      } catch (error) {
        console.log("Error fetching global flat categories", error);
      }
    };

    if (propertyId) fetchFromProperty();
    else fetchGlobal();
  }, [dispatch, propertyId]);

  // Helper: extract bedroom number from category name/feature.
  const extractBedrooms = (category: FlatType): number | null => {
    const textToSearch =
      (category?.name ? String(category.name) : "") +
      " " +
      (category?.feature ? String(category.feature) : "");
    const digitMatch = textToSearch.match(/\b(\d{1,2})\b/);
    if (digitMatch) {
      const num = parseInt(digitMatch[1], 10);
      if (!Number.isNaN(num)) return num;
    }
    return null;
  };

  // Sort by bedroom count ascending; non-numeric (Penthouse, Studio) last.
  // Dedupe already happened upstream — this is just sort.
  const uniqueFlatCategories = useMemo(() => {
    const sorted = [...flatCategories];
    sorted.sort((a, b) => {
      const aNum = extractBedrooms(a);
      const bNum = extractBedrooms(b);
      if (aNum !== null && bNum !== null) {
        return aNum - bNum || String(a.name).localeCompare(String(b.name));
      }
      if (aNum !== null && bNum === null) return -1;
      if (aNum === null && bNum !== null) return 1;
      const aKey = `${a?.name || ""} ${a?.feature || ""}`.trim();
      const bKey = `${b?.name || ""} ${b?.feature || ""}`.trim();
      return aKey.localeCompare(bKey);
    });
    return sorted;
  }, [flatCategories]);

  const showflatChecked = watch("showflat_appointment", false);

  useEffect(() => {
    if (!showflatChecked) {
      setValue("date", "");
      setValue("time", "");
    }
  }, [showflatChecked, setValue]);

  // click outside for details
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailsRef.current &&
        !detailsRef.current.contains(event.target as Node)
      ) {
        detailsRef.current.open = false;
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmitEnquiryForm = async (data: EnquiryValidationType) => {
    const sourceUrl =
      typeof window !== "undefined" ? window.location.href : undefined;

    const basePayload = {
      name: data.name,
      email: data.email,
      mobile_number: data.mobile_number,
      brochure: data.brochure ?? false,
      price_list: data.price_list ?? false,
      flat_type_id: data.flat_type_id || [],
      ...(propertyId ? { property: propertyId } : {}),
      ...(sourceUrl ? { source_url: sourceUrl } : {}),
      ...(unitInterest ? { unit_interest: unitInterest } : {}),
    };

    const enquiryPayload: EnquiryPayload = data?.showflat_appointment
      ? {
          ...basePayload,
          showflat_appointment: {
            showflat_appointment: data.showflat_appointment ?? false,
            date: data.showflat_appointment ? data.date : null,
            time: data.showflat_appointment ? data.time : "",
          },
        }
      : basePayload;

    try {
      const response = await dispatch(postEnquiry(enquiryPayload)).unwrap();
      if (response?.status === 201) {
        // Google Ads + Meta lead, only on a confirmed 201, never on page load.
        reportLead("Enquiry form");
        toast.success("Enquiry sent successfully!");
        reset();
        setIsPrivacyChecked(false);
        onClose();
      }
    } catch (error) {
      console.log("Error while post Enquiry", error);
      toast.error("Failed to send enquiry. Please try again.");
    }
  };

  const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPrivacyChecked(e.target.checked);
  };

  return (
    <>
      <form
        className="space-y-2"
        onSubmit={handleSubmit(handleSubmitEnquiryForm)}
      >
        {unitInterest && (
          <div className="mb-[16px] rounded-lg bg-[#FFF8E0] border border-[#FFCC00] p-3 text-[14px] text-[#3A3A3A]">
            <p className="font-semibold text-[#0B0B0B] mb-1">
              Enquiring about Unit {unitInterest.unit}
            </p>
            <p>
              {[
                unitInterest.type,
                unitInterest.plan ? `Plan ${unitInterest.plan}` : null,
                unitInterest.area ? `${unitInterest.area} sqft` : null,
                unitInterest.price
                  ? `$${unitInterest.price.toLocaleString()}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        )}
        <div className="space-y-2 mb-[24px]">
          <p className="font-medium text-[18px] text-[#0B0B0B]">
            I am interested in
          </p>
          <div className="space-y-2">
            <label className="flex items-center text-[#0B0B0B] text-[16px] gap-2">
              <input type="checkbox" className="" {...register("brochure")} />{" "}
              Brochure
            </label>
            <label className="flex items-center gap-2 text-[#0B0B0B] text-[16px]">
              <input
                type="checkbox"
                className="rounded text-[#0094FF] focus:ring-[#0094FF]"
                {...register("price_list")}
              />{" "}
              Price List
            </label>
            <label className="flex items-center gap-2 text-[#0B0B0B] text-[16px]">
              <input
                type="checkbox"
                className="rounded text-[#0094FF] focus:ring-[#0094FF]"
                checked={showflatChecked}
                {...register("showflat_appointment")}
              />{" "}
              Showflat Appointment
            </label>
          </div>
        </div>

        <div className="flex items-center border border-[#7D7999] rounded-full px-3 py-2">
          <Image src={user} alt="" className="mr-3" />
          <input
            type="text"
            placeholder="Full Name *"
            className="w-full outline-none bg-transparent"
            {...register("name")}
          />
        </div>
        {errors.name && (
          <p className="text-red-500 ml-5 text-sm">{errors.name.message}</p>
        )}

        <div className="flex items-center border border-[#7D7999] rounded-full px-3 py-2">
          <Image src={mail} alt="" className="mr-3" />
          <input
            type="email"
            placeholder="E-mail Address *"
            className="w-full outline-none bg-transparent"
            {...register("email")}
          />
        </div>
        {errors.email && (
          <p className="text-red-500 ml-5 text-sm">{errors.email.message}</p>
        )}

        <div className="flex items-center border border-[#7D7999] rounded-full px-3 py-2">
          <Image src={phone} alt="" className="mr-3" />
          <input
            type="tel"
            placeholder="Contact Number *"
            className="w-full outline-none bg-transparent"
            {...register("mobile_number")}
          />
        </div>
        {errors.mobile_number && (
          <p className="text-red-500 ml-5 text-sm">
            {errors.mobile_number.message}
          </p>
        )}

        {showflatChecked && (
          <>
            <div className="flex items-center border border-[#7D7999] rounded-full px-3 py-2">
              <input
                type="date"
                className="w-full outline-none appearance-none bg-transparent  placeholder-[#7D7999]"
                placeholder="Choose appointment date *"
                {...register("date")}
              />
            </div>
            {errors.date && (
              <p className="text-red-500 ml-5 text-sm">{errors.date.message}</p>
            )}
            <div className="flex items-center border border-[#7D7999] rounded-full px-3 py-2">
              <input
                type="time"
                className="w-full outline-none appearance-none bg-transparent  placeholder-[#7D7999]"
                placeholder="Choose appointment time *"
                {...register("time")}
              />
            </div>
            {errors.time && (
              <p className="text-red-500 ml-5 text-sm">{errors.time.message}</p>
            )}
          </>
        )}

        {/* Only render the unit-types section when this property has units to
            pick from. For pre-launch properties, lead capture works without
            forcing a unit selection. */}
        {uniqueFlatCategories.length > 0 && (
          <div className="space-y-5">
            <details ref={detailsRef} className="w-full border-[#7d7999] rounded-full">
              <summary className="px-3 py-2 cursor-pointer font-medium border border-[#7d7999] rounded-full">
                I am interested in the following unit types
              </summary>

              <div className="p-3 space-y-2">
                {uniqueFlatCategories.map((category) => {
                  const idStr = category?._id
                    ? String(category._id)
                    : `${category?.name}-${category?.feature || ""}`;
                  return (
                    <label key={idStr} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="rounded text-[#0094FF] focus:ring-[#0094FF]"
                        value={idStr}
                        {...register("flat_type_id")}
                      />
                      {category?.name}
                      {category?.feature ? ` + ${category?.feature}` : ""}
                    </label>
                  );
                })}
              </div>
            </details>
          </div>
        )}

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            onChange={handlePrivacyChange}
            checked={isPrivacyChecked}
            className="mt-1.5 rounded text-[#0094FF] focus:ring-[#0094FF]"
          />

          <span className="text-[16px] font-medium text-[#0B0B0B]">
            <span className="text-red-500">*</span>{" "}
            {isExpanded ? text : words.slice(0, 10).join(" ") + "..."}
            {words.length > 10 && (
              <span
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-[#0094FF] ml-1 underline focus:outline-none cursor-pointer"
              >
                {isExpanded ? "Read Less" : "Read More"}
              </span>
            )}
          </span>
        </div>

        <button
          type="submit"
          className={`px-5 rounded-full py-2 font-semibold custom-shadow-2 transition-all ${
            isPrivacyChecked && isValid
              ? "bg-[#FFCC00] hover:bg-[#E6B800] text-[#120F30]"
              : "bg-[#FFCC00]/30 text-[#120F30]/60 cursor-not-allowed"
          }`}
          disabled={!isPrivacyChecked || !isValid}
        >
          Yes Please
        </button>
      </form>
    </>
  );
};

export default EnquiryForm;
